// PORTED COPY — A20 W8. Original: TPRM7 packages/ui/src/reporter/StudioPanel.tsx
// (@vendorwatch/ui). The licence repo cannot import @vendorwatch/ui — separate
// repo, separate lockfile, and a Vite alias of '@' → './src' only — so it
// carries a copy, the same way types.ts / strings.ts / useRecorder.ts /
// ReportProvider.tsx / ReportDialog.tsx already do. Keep the workflow and the
// honesty rules diffable against the original; changes to those belong upstream
// first. FOUR deliberate divergences, each named where it happens:
//
//   1. <Modal>/<Button> from @vendorwatch/ui  → <Dialog>/<Btn> from this repo
//      (the same Radix primitive underneath — identical swap to ReportDialog).
//   2. This file also carries the SESSION HOST (`StudioReporter`) and the
//      transport, which upstream leaves to the host app. See "one recorder".
//   3. Copy for the studio keys this repo's ported strings.ts predates is
//      local and English-only. See STUDIO_COPY.
//   4. The result is rendered from the SAVED ROW, not from a bare `ok`. See
//      "what the result actually says".
//
// ── OPERATOR DECISION: STUDIO'S UI LIVES ONLY HERE ──────────────────────────
// Not in apps/admin. That app's AdminReporter.tsx already records the decision
// on its own side ("`studioAvailable` is deliberately not set … the licence
// console owns Studio"), so there is nothing to remove there.
//
// ── THE LIMITATION, STATED IN THE UI AND NOT LEFT TO BE DISCOVERED ──────────
// rrweb records THE PAGE IT RUNS IN. This panel runs in the licence console, so
// it can record walkthroughs OF THE LICENCE CONSOLE and nothing else. Recording
// the tenant app would need a panel mounted inside the tenant app, which is
// deliberately out of scope. STUDIO_COPY.scope says exactly that, on screen,
// before the author starts — because finding out afterwards costs them a whole
// take.
//
// ── ONE RECORDER, EVER (rrweb 2.1.1 makes this non-negotiable) ──────────────
// rrweb's `record()` assigns a MODULE-LEVEL `wrappedEmit` (rrweb.js:14313) that
// every observer it installs closes over. A second concurrent `record()` call
// therefore does not start a second recorder — it HIJACKS the first: both sets
// of observers emit into the newest buffer, and when the newer recorder stops,
// the older one is left emitting into a dead closure. The bug reporter would go
// silently deaf for the rest of the session.
//
// So the console never runs two. `StudioReporter` and `ConsoleReporter` are
// SWAPPED in AdminHeader, not nested: starting a walkthrough unmounts the bug
// reporter (stopping its rrweb and restoring the fetch/XHR shim) and mounts this
// one; ending it swaps back. React runs the removed subtree's cleanups before
// the added subtree's effects in the same commit, so the shims unwind in the
// right order and the pristine `window.fetch` is restored before the studio
// recorder patches it again.
//
// The visible consequence, and it is the honest one: while a walkthrough is
// recording, "Report a problem" is not in the header. You are recording a
// walkthrough, not filing a bug, and the rolling bug buffer is discarded — it is
// a 30-second ring buffer, so there was nothing there worth keeping.
//
// ── WHERE THE SAVE GOES ─────────────────────────────────────────────────────
// POST /api/v1/help-assets, never /reports. The bug ingest deliberately rejects
// `mode: 'studio'` with WRONG_ENDPOINT (bug-report.service.ts) and the help-asset
// ingest rejects `mode: 'report'` the same way, so neither can silently file the
// other's content. Both doors run on the caller's own Supabase session, which is
// the point: the studio fence is a check on a VERIFIED HUMAN, and an API key
// would carry nobody to check.
//
// ── THE SECURITY PROPERTY, AND WHY THIS COMPONENT IS NOT IT ─────────────────
// Studio may record UNMASKED. `allowUnmasked` here, `config.studioAvailable`,
// and mayAuthorWalkthroughs() in lib/persona.ts decide only whether an
// affordance is drawn. assertStudioAuthor, on the verified session, at every
// write, is the fence. Hiding a button is not a boundary; the server refusing
// the write is.
//
// ── TWO HONESTY RULES, inherited from ReportDialog ──────────────────────────
//   1. "Stop" and "Save" are ONE action. There is no window in which the panel
//      claims to have stopped while the buffer keeps filling.
//   2. The submit result is rendered verbatim, failures included, with the
//      author's name and notes still on screen.

import * as React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Btn } from '@/components/shared/primitives';
import { apiRequest, ApiError } from '@/lib/supabase';
import { ReportProvider, useReporter } from './ReportProvider';
import type { ReportEnvelope, ReporterConfig, SubmitResult } from './types';

// ---------------------------------------------------------------------------
// Copy this repo's ported catalogue does not have yet.
//
// strings.ts here was ported at W2b and carries six studio keys; upstream has
// grown to fifteen since. That file is a PORT — it is kept byte-comparable with
// TPRM7's so `diff` stays the review tool — so the missing keys belong upstream
// first and in the port second, which is not this slice's file to change. Until
// then this copy is English-only, and `t()` is still used for every key the
// catalogue does have, so the six that ARE translated stay translated.
//
// `scope` and `unmask.warning` are console-specific and would be WRONG upstream:
// the tenant apps do not display other customers' data, and this console does.
// ---------------------------------------------------------------------------
const STUDIO_COPY = {
  notesLabel: 'What this walkthrough shows',
  notesPlaceholder: 'What is being demonstrated, and who it is for.',
  stop: 'Stop and save',
  discard: 'Discard',
  recording: 'Recording walkthrough · {elapsed}',
  errorName: 'Name the walkthrough before recording.',
  errorNotes: 'Describe what this walkthrough shows.',
  hintTab: 'The recording lives only in this tab. Reloading the page ends it and nothing is kept.',
  scope:
    'Records THIS console only. The capture engine records the page it runs in, so a walkthrough here shows the licence console — not the customer-facing app, which would need its own panel.',
  unmaskLabel: 'Record visible text',
  unmaskHint:
    'Permitted only from the platform workspace or a designated demo workspace — the server re-checks and refuses the save otherwise.',
  unmaskWarning:
    'This console displays real customer names, licence identifiers and email addresses. An unmasked walkthrough captures whatever is on screen. Record unmasked only on screens you would publish as they are.',
  resultOk: 'Saved as a draft help asset.',
  resultNoRecording:
    'Saved as a draft, but the recording did NOT store — so this draft cannot be published. Nothing else was lost; record it again.',
  resultNoCapture:
    'Saved as a draft with no recording attached, so it cannot be published yet.',
  captureDropped:
    'The recording was too large to send and was dropped. Only the name and notes were saved — record a shorter walkthrough.',
  // The catalogue's 'result.error' says "could not send the REPORT", which is
  // the wrong noun here and the one place that would read as a mis-wire.
  saveError: 'Could not save the walkthrough: {error}',
  saving: 'Saving…',
} as const;

/** Fill `{elapsed}` etc. the same way makeTranslator does, without a catalogue. */
function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

// ---------------------------------------------------------------------------
// The session switch.
//
// A module-level store, deliberately, and it is worth saying why rather than
// leaving the next reader to assume React context was forgotten. The recorder
// must survive route changes, so the provider has to live in the persistent
// chrome (AdminHeader). The thing that opens it — a NAV entry's page — renders
// in <main>, which is AdminHeader's SIBLING, not its descendant. A context
// spanning both would have to be provided in PortalShell (App.tsx), which this
// slice does not own. One boolean and a Set of listeners is a smaller, more
// obvious dependency than a shared parent.
//
// Exactly one <StudioReporter> subscribes (AdminHeader mounts one), so there is
// no ambiguity about who responds.
// ---------------------------------------------------------------------------
type SessionListener = (active: boolean) => void;
const sessionListeners = new Set<SessionListener>();
let sessionActive = false;

function setSession(next: boolean): void {
  if (sessionActive === next) return;
  sessionActive = next;
  sessionListeners.forEach((fn) => fn(next));
}

/** Open the studio. Safe to call from anywhere in the authenticated console. */
export function openStudio(): void {
  setSession(true);
}

/** Close it. Exported for tests and for a caller that needs to force an exit. */
export function closeStudio(): void {
  setSession(false);
}

/** Subscribe to the session flag. */
export function useStudioSession(): boolean {
  const [active, setActive] = React.useState(sessionActive);
  React.useEffect(() => {
    // Re-read on subscribe: the flag may have flipped between render and effect.
    setActive(sessionActive);
    sessionListeners.add(setActive);
    return () => {
      sessionListeners.delete(setActive);
    };
  }, []);
  return active;
}

// ---------------------------------------------------------------------------
// The transport
// ---------------------------------------------------------------------------

/** What POST /help-assets returns: `{ data: <row>, slug }` (help-assets.routes.ts). */
interface HelpAssetResponse {
  slug?: string;
  data?: {
    slug?: string;
    name?: string;
    capture_received?: boolean;
    capture_available?: boolean;
    recording_bytes?: number | null;
  };
}

/**
 * The saved row, reduced to what the panel must tell the author.
 *
 * `recording_path` is NOT here and must never be: it is a storage object path,
 * the service's PUBLIC_COLUMNS deliberately omit it, and a path in a browser is
 * the operator's masking requirement broken.
 */
export interface SavedWalkthrough {
  slug: string;
  captureReceived: boolean;
  captureAvailable: boolean;
}

function readSaved(payload: HelpAssetResponse | null | undefined): SavedWalkthrough | null {
  const row = payload?.data;
  const slug = row?.slug ?? payload?.slug;
  if (!slug) return null;
  return {
    slug,
    // `=== true` throughout: a field the server did not send must read as
    // "no", never as "probably yes". This is the honesty rule at the last hop.
    captureReceived: row?.capture_received === true,
    captureAvailable: row?.capture_available === true,
  };
}

/**
 * Relay one studio envelope to the licence backend.
 *
 * Structured failure, never a throw, for the same reason as ConsoleReporter's
 * transport: the panel renders the reason with the author's text still on
 * screen. There is no branch that reports success without a 2xx.
 */
async function postWalkthrough(
  envelope: ReportEnvelope,
): Promise<{ result: SubmitResult; saved: SavedWalkthrough | null }> {
  try {
    const res = await apiRequest<HelpAssetResponse>('/help-assets', {
      method: 'POST',
      body: JSON.stringify(envelope),
    });
    const saved = readSaved(res);
    if (!saved) {
      // A 2xx we cannot read is not a success we can report. Same posture as
      // pages/support/api.ts's unwrapList: an unrecognised shape is an error,
      // never an empty state dressed up as one.
      return {
        result: {
          ok: false,
          error: 'The licence server accepted the walkthrough but returned an unrecognised shape.',
        },
        saved: null,
      };
    }
    return { result: { ok: true, ticketRef: saved.slug }, saved };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 0) {
        return {
          result: {
            ok: false,
            error: 'Could not reach the licence server. Check your connection and try again.',
          },
          saved: null,
        };
      }
      // 403 lands here verbatim — including assertStudioAuthor's refusal, which
      // is the whole point of showing the server's own message rather than a
      // guess at what went wrong.
      return { result: { ok: false, error: err.message }, saved: null };
    }
    return {
      result: { ok: false, error: err instanceof Error ? err.message : 'network error' },
      saved: null,
    };
  }
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.2,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

/** mm:ss for the recording pill. */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

type Phase = 'setup' | 'recording' | 'saving' | 'done';

export interface StudioPanelProps {
  /** Controlled open state of the setup/result dialog. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The session is over — tear the studio provider down and give the bug
   * reporter its recorder back. Called on the two paths that end a session:
   * backing out of setup, and closing the result.
   *
   * NOT part of the upstream props. Upstream the panel lives permanently in app
   * chrome alongside the bug reporter; here the two swap, so somebody has to say
   * when the swap reverses.
   */
  onExit: () => void;
  /** The row the last save returned, for an honest result line. */
  saved?: SavedWalkthrough | null;
  /**
   * Offer the "record visible text" option.
   *
   * A convenience only: the licence server re-checks the caller's principal on
   * every save and rejects an unmasked walkthrough from anywhere but the
   * platform desk or a designated demo org.
   */
  allowUnmasked?: boolean;
}

/**
 * Record a product walkthrough and save it as a help asset.
 *
 * Renders nothing unless the host opted in via `ReporterConfig.studioAvailable`.
 *
 * The dialog is open only while setting up or reading the result; during the
 * recording itself it closes to a small fixed pill so the author can actually
 * drive the product. Both pieces carry `ps-no-capture`, so the studio chrome
 * never appears in its own walkthrough.
 */
export function StudioPanel({
  open,
  onOpenChange,
  onExit,
  saved = null,
  allowUnmasked = false,
}: StudioPanelProps) {
  const { config, recorder, setOpen, submit, t, lastCaptureDropped } = useReporter();

  const [phase, setPhase] = React.useState<Phase>('setup');
  const [name, setName] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [unmasked, setUnmasked] = React.useState(false);
  const [touched, setTouched] = React.useState(false);
  const [result, setResult] = React.useState<SubmitResult | null>(null);
  const [startedAt, setStartedAt] = React.useState<number | null>(null);
  const [elapsed, setElapsed] = React.useState(0);

  const enabled = config.studioAvailable === true;

  // Opening the panel puts the PROVIDER into studio mode: the recorder reads
  // `mode` for its window length (30s → 5min) and `submit` stamps it on the
  // envelope, which is what routes the save away from the bug endpoint. Passing
  // `false` for the open flag deliberately leaves ReportDialog shut — studio has
  // its own dialog and the two must never be on screen together. (In this
  // console ReportDialog is not even mounted while studio is up, because the two
  // reporters swap; the call is kept identical to upstream anyway.)
  //
  // The hand-back to 'report' is done explicitly on each exit path rather than
  // in this effect's cleanup: a cleanup would fire the moment the dialog closes
  // to start recording, dropping the recorder back to a 30-second window
  // mid-walkthrough.
  React.useEffect(() => {
    if (enabled && open) setOpen(false, 'studio');
  }, [enabled, open, setOpen]);

  // Safety net for the one exit path the buttons cannot cover: the panel being
  // unmounted mid-walkthrough (a hard navigation, a layout swap). `setOpen` is
  // stable, so this runs on unmount only.
  //
  // It ALSO re-masks, for the same reason ReportDialog now does on close: an
  // unmasked walkthrough that loses its panel can never be finished or
  // discarded, so without this the recorder stayed unmasked for the rest of the
  // session — and the next ordinary bug report would carry real customer text
  // while its ticket row said `masked = true`, because that column is derived
  // from the dialog's consent checkbox and not from the recorder. A ticket
  // labelled redacted that is not redacted is worse than an unmasked one.
  const restartMasked = recorder.restart; // stable useCallback; `recorder` is a fresh object each render
  React.useEffect(
    () => () => {
      setOpen(false, 'report');
      restartMasked({ masked: true });
    },
    [setOpen, restartMasked]
  );

  // Tick the elapsed counter while recording. One timer, cleared on stop.
  React.useEffect(() => {
    if (phase !== 'recording' || startedAt === null) return undefined;
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 1000);
    return () => clearInterval(id);
  }, [phase, startedAt]);

  if (!enabled) return null;

  const invalidName = name.trim().length === 0;
  const invalidNotes = notes.trim().length === 0;
  const invalid = invalidName || invalidNotes;

  const onStart = () => {
    setTouched(true);
    if (invalid) return;
    // Masking is fixed at record() start, so a fresh, correctly-masked buffer is
    // the only way to honour the author's choice — and it also discards whatever
    // the rolling buffer happened to be holding, so a walkthrough starts at the
    // moment the author said "start" and not a minute earlier.
    recorder.restart({ masked: !unmasked });
    setResult(null);
    setStartedAt(Date.now());
    setElapsed(0);
    setPhase('recording');
    onOpenChange(false);
  };

  // Stop AND save, in one action — see honesty rule 1 in the file header.
  const onFinish = async () => {
    setPhase('saving');
    onOpenChange(true);
    const res = await submit({
      title: name.trim(),
      description: notes.trim(),
      // `severity` is meaningless for a walkthrough; the envelope carries it
      // because every capture shares one body shape, and the help-asset ingest
      // ignores it. 'question' is the one member that does not read as a defect.
      severity: 'question',
      includeVisibleText: unmasked,
      assetName: name.trim(),
    });
    setResult(res);
    setStartedAt(null);
    setPhase('done');
    // Hand the provider back to ordinary bug reporting.
    setOpen(false, 'report');
  };

  const onDiscard = () => {
    // Nothing to delete: nothing left the browser. Restarting the recorder
    // masked also drops the unmasked buffer immediately rather than leaving it
    // sitting in memory until the next checkout.
    recorder.restart({ masked: true });
    setStartedAt(null);
    setElapsed(0);
    setPhase('setup');
    // Back to setup with the session still OPEN — discarding a take is not
    // leaving the studio. Upstream hands the mode back to 'report' here because
    // upstream's panel lives permanently beside the bug reporter; doing that in
    // this console would drop the recorder to a 30-second window and then
    // immediately restart it at five minutes when the dialog reopens, for no
    // reason. The mode is handed back on the two paths that really end the
    // session: onCancel and onCloseResult.
    onOpenChange(true);
  };

  const onCloseResult = () => {
    setPhase('setup');
    setName('');
    setNotes('');
    setUnmasked(false);
    setTouched(false);
    setResult(null);
    onOpenChange(false);
    setOpen(false, 'report');
    onExit();
  };

  /** Backing out of setup. Nothing was recorded yet; just end the session. */
  const onCancel = () => {
    onOpenChange(false);
    setOpen(false, 'report');
    onExit();
  };

  /**
   * What the result actually says.
   *
   * NOT `ok ? 'Saved.' : error`. A save can succeed while the RECORDING does
   * not: the row is inserted, the bytes then fail to reach storage, and the
   * server answers 201 with `capture_available: false` — which is the honest
   * thing for it to do, and would be a lie for this panel to render as an
   * unqualified success. The author needs to know the draft cannot be published,
   * because the difference costs them a re-take either way; discovering it later
   * costs them the take AND the trip.
   */
  const resultLine = (): string => {
    if (!result?.ok) return '';
    if (saved?.captureAvailable) return STUDIO_COPY.resultOk;
    if (saved?.captureReceived) return STUDIO_COPY.resultNoRecording;
    if (lastCaptureDropped) return STUDIO_COPY.captureDropped;
    return STUDIO_COPY.resultNoCapture;
  };

  const captureOk = !config.captureDisabled && !recorder.error;

  return (
    <>
      {phase === 'recording' && (
        <div
          className="ps-no-capture"
          role="status"
          style={{
            position: 'fixed',
            right: 18,
            bottom: 18,
            zIndex: 2147483000,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.22)',
            fontFamily: 'var(--font-sans)',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 9,
              height: 9,
              borderRadius: 999,
              background: 'var(--severity-critical)',
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
            {fill(STUDIO_COPY.recording, { elapsed: formatElapsed(elapsed) })}
          </span>
          <Btn kind="primary" sm onClick={onFinish}>
            {STUDIO_COPY.stop}
          </Btn>
          <Btn kind="ghost" sm onClick={onDiscard}>
            {STUDIO_COPY.discard}
          </Btn>
        </div>
      )}

      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (o) onOpenChange(true);
          // Esc/overlay must not close over an in-flight save: the session
          // teardown would land mid-submit and the author would lose the result.
          else if (phase === 'saving') return;
          else if (phase === 'done') onCloseResult();
          else onCancel();
        }}
      >
        <DialogContent style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
          {/* The studio's own chrome is never part of the walkthrough. */}
          <div className="ps-no-capture" style={{ display: 'grid', gap: 14 }}>
            <div>
              <DialogTitle style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                {t('studio.title')}
              </DialogTitle>
              <DialogDescription style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {t('studio.subtitle')}
              </DialogDescription>
            </div>

            {phase === 'done' && result?.ok ? (
              <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
                {resultLine()}
                {saved?.slug ? (
                  <span style={{ color: 'var(--text-muted)' }}> ({saved.slug})</span>
                ) : null}
              </p>
            ) : (
              <>
                {result && !result.ok && (
                  <div
                    role="alert"
                    style={{
                      padding: '9px 11px',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--severity-critical)',
                      background: 'var(--surface-sunken)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  >
                    {fill(STUDIO_COPY.saveError, { error: result.error })}
                  </div>
                )}

                <div>
                  <label style={fieldLabel} htmlFor="ps-studio-name">
                    {t('studio.name.label')}
                  </label>
                  <input
                    id="ps-studio-name"
                    style={inputStyle}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('studio.name.placeholder')}
                    maxLength={160}
                    aria-invalid={touched && invalidName}
                  />
                  {touched && invalidName && (
                    <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--severity-critical)' }}>
                      {STUDIO_COPY.errorName}
                    </p>
                  )}
                </div>

                <div>
                  <label style={fieldLabel} htmlFor="ps-studio-notes">
                    {STUDIO_COPY.notesLabel}
                  </label>
                  <textarea
                    id="ps-studio-notes"
                    style={{ ...inputStyle, minHeight: 84, resize: 'vertical' }}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={STUDIO_COPY.notesPlaceholder}
                    maxLength={5000}
                    aria-invalid={touched && invalidNotes}
                  />
                  {touched && invalidNotes && (
                    <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--severity-critical)' }}>
                      {STUDIO_COPY.errorNotes}
                    </p>
                  )}
                </div>

                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--surface-sunken)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <span style={{ ...fieldLabel, marginBottom: 4 }}>{t('capture.heading')}</span>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                    {captureOk ? STUDIO_COPY.hintTab : t('capture.off')}
                  </p>
                  {/* The accepted limitation, said out loud before the take. */}
                  <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {STUDIO_COPY.scope}
                  </p>

                  {allowUnmasked && (
                    <>
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 8,
                          marginTop: 10,
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={unmasked}
                          onChange={(e) => setUnmasked(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <span>
                          <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                            {STUDIO_COPY.unmaskLabel}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              fontSize: 12,
                              color: 'var(--text-muted)',
                              marginTop: 2,
                            }}
                          >
                            {STUDIO_COPY.unmaskHint}
                          </span>
                        </span>
                      </label>
                      {unmasked && (
                        <p
                          role="alert"
                          style={{
                            margin: '8px 0 0',
                            fontSize: 12.5,
                            color: 'var(--severity-critical)',
                          }}
                        >
                          {STUDIO_COPY.unmaskWarning}
                        </p>
                      )}
                    </>
                  )}
                </div>

                <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('privacy.note')}
                </p>
              </>
            )}

            {phase === 'done' && result?.ok ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Btn kind="primary" onClick={onCloseResult}>
                  {t('action.close')}
                </Btn>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Btn kind="secondary" onClick={onCancel} disabled={phase === 'saving'}>
                  {t('action.cancel')}
                </Btn>
                <Btn kind="primary" onClick={onStart} disabled={phase === 'saving'}>
                  {phase === 'saving' ? STUDIO_COPY.saving : t('studio.action.start')}
                </Btn>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// The session host
// ---------------------------------------------------------------------------

/**
 * Mounts the reporter context in STUDIO configuration plus the panel.
 *
 * Mounted by App.tsx's ReporterHost INSTEAD OF ConsoleReporter for the duration
 * of a session — see "one recorder, ever" in the file header. Nothing else may
 * mount it, and nothing may mount it alongside the bug reporter.
 *
 * A20 C3 — it takes `children` and wraps the shell, where it used to render a
 * panel and nothing else. The swap moved out of AdminHeader (a sibling of the
 * page content, so no boundary could see it) and up to the shell, so BOTH
 * providers now sit above the routed tree. Recording is unaffected: rrweb
 * records the document, not the subtree it was started from.
 */
export function StudioReporter({
  onExit,
  children,
}: {
  onExit: () => void;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(true);
  const [saved, setSaved] = React.useState<SavedWalkthrough | null>(null);

  // A session torn down without taking an exit path — signing out mid-recording,
  // a hard navigation — must not leave the module flag set. It outlives the
  // component (that is the whole point of it), so the next mount of AdminHeader
  // would find `sessionActive === true` and open the studio by itself, over a
  // user who never asked for it and possibly is not even allowed to author.
  // `closeStudio` short-circuits when the flag is already false, so the ordinary
  // exit paths do not double-fire.
  React.useEffect(() => () => closeStudio(), []);

  const config = React.useMemo<ReporterConfig>(
    () => ({
      // Unused by the custom transport, but the config type requires an endpoint
      // and a wrong-looking one would mislead the next reader.
      endpoint: '/api/v1/help-assets',
      surfaceKey: 'license.console',
      appVersion: import.meta.env.VITE_APP_VERSION as string | undefined,
      transport: async (envelope) => {
        const { result, saved: row } = await postWalkthrough(envelope);
        setSaved(row);
        return result;
      },
      // Identity the browser genuinely owns: nothing. The route stamps author,
      // role and org from the verified session — A20's posture throughout is
      // that identity comes from the token, never the payload.
      getIdentity: () => ({}),
      // Draws the panel. NOT an authorisation: assertStudioAuthor is.
      studioAvailable: true,
      // Lets an operator kill capture fleet-wide without redeploying the widget.
      // A walkthrough with capture off is a name and some notes, and the panel
      // says so rather than pretending it recorded something.
      captureDisabled: import.meta.env.VITE_REPORTER_CAPTURE === 'off',
    }),
    [],
  );

  return (
    <ReportProvider config={config}>
      {children}
      <StudioPanel
        open={open}
        onOpenChange={setOpen}
        onExit={onExit}
        saved={saved}
        // The console IS the platform desk's surface, so the option is offered
        // here. The server still decides: a super-admin sitting in a customer
        // org gets a 403 the panel renders verbatim.
        allowUnmasked
      />
    </ReportProvider>
  );
}

export default StudioPanel;
